import { useMemo } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import type { AnimationTiming } from 'recharts/types/util/types'
import { ChartCard } from './ChartCard'
import { getChartColorCss } from '../constants/chartColors'
import { CHART_ANIMATION } from '../constants/chartAnimation'
import type { AggregatedChartData } from '../types'

export interface AreaDistributionChartProps {
  readonly data: ReadonlyArray<AggregatedChartData>
  readonly title?: string
  readonly subtitle?: string
  readonly colorOffset?: number
}

/**
 * Area Distribution Chart - Bell curve visualization
 *
 * Smooth area chart for continuous distributions (e.g., age intervals).
 * Shows data as a bell curve with gradient fill.
 *
 * Features:
 * - Smooth monotone curve interpolation
 * - Gradient fill (Capsule DNA colors)
 * - Hover tooltip with value
 * - Animated entry (700ms bounce)
 */
export const AreaDistributionChart: React.FC<AreaDistributionChartProps> = ({
  data,
  title = 'Distribution',
  subtitle,
  colorOffset = 0,
}) => {
  const chartData = useMemo(() => {
    return data.map((item) => ({
      name: item.label,
      value: item.value,
    }))
  }, [data])

  // Get primary chart color as CSS string (with offset for variety)
  const primaryColor = getChartColorCss(colorOffset)

  if (chartData.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <div className="flex items-center justify-center min-h-[160px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    )
  }

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <defs>
            {/* Gradient fill for area */}
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.8} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0.1} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="currentColor"
            className="text-v11-border opacity-30"
            vertical={false}
          />

          <XAxis
            dataKey="name"
            stroke="currentColor"
            className="text-v11-text-muted text-xs"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
          />

          <YAxis
            stroke="currentColor"
            className="text-v11-text-muted text-xs"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={40}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--v11-surface))',
              border: '1px solid hsl(var(--v11-border))',
              borderRadius: '12px',
              padding: '8px 12px',
            }}
            labelStyle={{
              color: 'hsl(var(--v11-text-primary))',
              fontWeight: 600,
              marginBottom: '4px',
            }}
            itemStyle={{
              color: 'hsl(var(--v11-text-secondary))',
            }}
            cursor={{ stroke: primaryColor, strokeWidth: 2, strokeDasharray: '5 5' }}
          />

          <Area
            type="monotone"
            dataKey="value"
            stroke={primaryColor}
            strokeWidth={3}
            fill="url(#areaGradient)"
            animationDuration={CHART_ANIMATION.duration}
            animationEasing={CHART_ANIMATION.easing as AnimationTiming}
            isAnimationActive={true}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}
